// Template3.tsx - Updated with Photo Synchronization Fixes
"use client";

import React, { useRef, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { ImageCropModal } from "@/components/ui/ImageCropModal";
import {
  MapPin,
  Bed,
  Bath,
  Square,
  Phone,
  Mail,
  Upload,
  Image as ImageIcon,
  Crop,
  X,
  Tag,
  Check,
  Loader2,
  Trash2,
  Building2,
  IdCard
} from "lucide-react";

import useTemplateBase, {
  mergePropertyListsWithCurrent,
  resolvePropertyListName,
} from "./TemplateBase";
import { useToast } from "@/hooks/use-toast";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import openHouseBadge from "../../assets/star_red.png";

interface PropertyList {
  id: number;
  list_name: string;
}

interface Template3FlyerData {
  tag: string;
  price: number | null;
  headline: string;
  address: string;
  neighborhood?: string;
  bedrooms: number | null;
  bathrooms: number | null;
  sqft: number | null;
  area?: number | null;
  description: string;
  features: { id?: number; feature_name: string }[];
  agentName?: string;
  agentPhone?: string;
  agentEmail?: string;
  heroImage: string;
  secondaryImage?: string;
  photo2?: string;
  photo3?: string;
  photo4?: string;
  photos?: string[];
  list_name?: string;
  customData?: string;
}

interface Property {
  id: number;
  propertyListType?: {
    id: number;
    list_name: string;
    custom_template_data?: Record<string, any>;
  };
  title: string;
  listing_id?: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  price?: number | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  area?: number | null;
  description?: string | null;
  text_section?: string | null;
  image?: string | null;
  original_image?: string | null;
  photos?: string[] | null;
  // Keyed by slot name (e.g. "photo2"), not array position — see TemplateBase.tsx's updatePhotoAtIndex.
  original_photos?: Record<string, string> | null;
  is_open_house?: boolean;
}

interface Template3Props {
  property?: Property | null;
  propertyLists?: PropertyList[];
  keyFeatures?: any[];
  onDirtyChange?: (dirty: boolean) => void;
  onChange?: (data: Partial<Template3FlyerData>) => void;
  onPropertyListChange?: (selectedList: string) => void;
  selectedPropertyList?: string;
  onPropertyListCreated?: (newList: PropertyList) => void;
  selectedList?: string; // ✅ ADD THIS PROP - same as template1/template2
  readOnly?: boolean;
}

// ─── Helper: route already-saved (non data:/blob:) S3 image URLs through the
// backend image proxy before feeding them to the crop modal's <canvas>, so the
// export doesn't fail with a cross-origin "tainted canvas" error. Mirrors the
// same helper in Template1.tsx.
const getEditableImageUrl = (url: string): string => {
  if (!url || url.startsWith("data:") || url.startsWith("blob:")) return url;
  try {
    const parsed = new URL(url, window.location.origin);
    const needsProxy =
      parsed.hostname.includes("amazonaws.com") ||
      parsed.hostname.includes("mylistiq-production.s3");
    if (!needsProxy) return url;
    return `${endpoints.image_proxy}?url=${encodeURIComponent(url)}`;
  } catch {
    return url;
  }
};

const Template3: React.FC<Template3Props> = ({
  property,
  propertyLists = [],
  keyFeatures = [],
  onDirtyChange,
  onChange,
  onPropertyListChange,
  selectedPropertyList: externalSelectedList,
  onPropertyListCreated,
  selectedList: viewSelectedList, // ✅ ADD THIS - same as template1/template2
  readOnly = false,
}) => {
  const { toast } = useToast();

  const [cropModal, setCropModal] = useState<{ open: boolean; imageUrl: string }>({
    open: false,
    imageUrl: "",
  });

  // Tracks the untouched "original" hero photo for this editing session, so
  // Edit Image always re-crops from the full source rather than whatever is
  // currently displayed. Mirrors the same state in Template1.tsx.
  const [originalHeroImage, setOriginalHeroImage] = useState<string>(
    property?.original_image || ""
  );

  // ✅ Photo refs for all photos (same as Template7)
  const photo2Ref = useRef<HTMLInputElement>(null);
  const photo3Ref = useRef<HTMLInputElement>(null);
  const photo4Ref = useRef<HTMLInputElement>(null);
  // Container refs + resolution hints (implemented like Template4)
  const heroContainerRef = useRef<HTMLDivElement | null>(null);
  const secondaryContainerRef = useRef<HTMLDivElement | null>(null);
  const [boxHeroRes, setBoxHeroRes] = useState<string | null>(null);
  const [boxSecondaryRes, setBoxSecondaryRes] = useState<string | null>(null);
  const RESOLUTION_HINT =
    "For best results, upload an image matching the box resolution.";

  // Custom list creation state
  const [isCustomList, setIsCustomList] = useState<boolean>(false);
  const [customListName, setCustomListName] = useState<string>("");
  const [isSavingCustomList, setIsSavingCustomList] = useState(false);
  const [localPropertyLists, setLocalPropertyLists] =
    useState<PropertyList[]>(propertyLists);
  const [localSelectedListOverride, setLocalSelectedListOverride] = useState<
    string | null
  >(null);

  // Custom fields editor state
  const [editingCustom3, setEditingCustom3] = useState(false);
  const [customFields3, setCustomFields3] = useState<
    { key: string; value: string }[]
  >([]);

  // ✅ USE TEMPLATE BASE HOOK - Now includes updatePhotoAtIndex and removePhotoAtIndex
  const {
    flyerData,
    setFlyerData,
    selectedPropertyList,
    updatePropertyList,
    fileInputRef,
    agentData,
    handleInputChange,
    addFeature,
    handleFeatureChange,
    handleFeatureBlur,
    handleFeatureDelete,
    updatePhotoAtIndex,
    removePhotoAtIndex,
    uploadCroppedPhoto,
    saveCustomTemplateData,
  } = useTemplateBase({
    property,
    propertyLists: localPropertyLists,
    keyFeatures,
    selectedPropertyList: externalSelectedList,
    onDirtyChange,
    onChange,
    onPropertyListChange,
  });

  // Sync local property lists when prop changes
  useEffect(() => {
    setLocalPropertyLists(propertyLists);
  }, [propertyLists]);

  // Reset local override when property changes
  useEffect(() => {
    setLocalSelectedListOverride(null);
  }, [property?.id]);

  useEffect(() => {
    setOriginalHeroImage(property?.original_image || "");
  }, [property?.id]);

  // Measure image container pixel dimensions for resolution suggestions
  useEffect(() => {
    const measure = () => {
      try {
        if (heroContainerRef.current) {
          const w = Math.round(heroContainerRef.current.clientWidth);
          const h = Math.round(heroContainerRef.current.clientHeight);
          setBoxHeroRes(`${w}×${h}`);
        }
        if (secondaryContainerRef.current) {
          const w2 = Math.round(secondaryContainerRef.current.clientWidth);
          const h2 = Math.round(secondaryContainerRef.current.clientHeight);
          setBoxSecondaryRes(`${w2}×${h2}`);
        }
      } catch {
        // ignore measurement errors
      }
    };

    measure();
    const onResize = () => measure();
    window.addEventListener("resize", onResize);

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => measure());
      if (heroContainerRef.current) ro.observe(heroContainerRef.current);
      if (secondaryContainerRef.current) ro.observe(secondaryContainerRef.current);
    }

    return () => {
      window.removeEventListener("resize", onResize);
      if (ro) ro.disconnect();
    };
  }, []);

  // Initialize custom fields from property
  useEffect(() => {
    const existingCustomData =
      property?.propertyListType?.custom_template_data?.template3 || [];
    setCustomFields3(
      Array.isArray(existingCustomData) ? existingCustomData : []
    );
  }, [property?.propertyListType?.custom_template_data?.template3]);

  // ✅ FIX: Determine currently selected list - SAME LOGIC AS TEMPLATE1/TEMPLATE2
  const selectedList =
    localSelectedListOverride ??
    viewSelectedList ?? // ✅ ADD THIS - was missing!
    externalSelectedList ??
    selectedPropertyList ??
    property?.propertyListType?.list_name ??
    "Just Listed";

  // Combine prop lists with local lists, provide defaults if empty
  const allLists = mergePropertyListsWithCurrent(
    localPropertyLists.length > 0
      ? localPropertyLists
      : [
        { id: 1, list_name: "Just Listed" },
        { id: 2, list_name: "Priced to Sell" },
        { id: 3, list_name: "Open House" },
        { id: 4, list_name: "Seller Motivated" },
      ],
    property?.propertyListType
  );

  const listSelectValue =
    resolvePropertyListName(
      selectedList,
      property?.propertyListType?.list_name
    ) || "Just Listed";

  // Custom fields editor functions
  const addCustomField3 = () => {
    if (readOnly) return;
    setCustomFields3((p) => [...p, { key: "", value: "" }]);
  };

  const updateCustomField3 = (i: number, field: "key" | "value", v: string) => {
    if (readOnly) return;
    setCustomFields3((p) => {
      const n = [...p];
      n[i] = { ...n[i], [field]: v };
      return n;
    });
  };

  const removeCustomField3 = (i: number) => {
    if (readOnly) return;
    setCustomFields3((p) => p.filter((_, idx) => idx !== i));
  };

  const saveCustomForTemplate3 = async () => {
    if (readOnly) return;
    try {
      if (typeof saveCustomTemplateData === "function") {
        await saveCustomTemplateData(
          "template3",
          customFields3.filter((f) => f.key || f.value)
        );
      } else {
        const token = localStorage.getItem("access_token");
        if (property?.propertyListType?.id) {
          await axiosInstance.patch(
            `${endpoints.propertylisttype}${property.propertyListType.id}/`,
            {
              custom_template_data: {
                ...(property.propertyListType.custom_template_data || {}),
                template3: customFields3,
              },
            },
            {
              headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            }
          );
        }
      }
      setEditingCustom3(false);
      toast({ title: "Saved", description: "Custom fields saved" });
    } catch (err) {
      console.error(err);
      toast({
        title: "Error",
        description: "Failed saving custom fields",
        variant: "destructive",
      });
    }
  };

  // === OPEN HOUSE LOGIC ===
  const [isOpenHouseConfirmed, setIsOpenHouseConfirmed] = useState<boolean>(
    property?.is_open_house ?? false
  );

  const handleOpenHouseChoice = async (confirmed: boolean) => {
    if (readOnly) return;
    if (!property?.id || selectedList !== "Open House") return;

    setIsOpenHouseConfirmed(confirmed);

    try {
      await axiosInstance.patch(`${endpoints.properties}${property.id}/`, {
        is_open_house: confirmed,
      });

      toast({
        title: confirmed ? "Open House Active" : "Open House Disabled",
        description: confirmed
          ? "Special styling enabled"
          : "Open House mode turned off",
        variant: "default",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to save choice",
        variant: "destructive",
      });
      setIsOpenHouseConfirmed(!confirmed);
    }
  };

  useEffect(() => {
    setIsOpenHouseConfirmed(property?.is_open_house ?? false);
  }, [property?.is_open_house]);

  // === SAVE CUSTOM LIST ===
  const saveCustomList = async () => {
    if (readOnly) return;

    const trimmedName = customListName.trim();

    if (!trimmedName) {
      toast({
        title: "Error",
        description: "Please enter a name for your custom tag",
        variant: "destructive",
      });
      return;
    }

    if (!property?.id) {
      toast({
        title: "Error",
        description: "No property selected. Please select a property first.",
        variant: "destructive",
      });
      return;
    }

    const existingList = localPropertyLists.find(
      (l) => l.list_name.toLowerCase() === trimmedName.toLowerCase()
    );

    if (existingList) {
      setLocalSelectedListOverride(existingList.list_name);
      updatePropertyList(existingList.list_name, existingList.id);
      setIsCustomList(false);
      setCustomListName("");

      toast({
        title: "List Selected",
        description: `"${existingList.list_name}" already exists and has been selected`,
      });
      return;
    }

    setIsSavingCustomList(true);

    try {
      const token = localStorage.getItem("access_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const createResponse = await axiosInstance.post(
        endpoints.propertylisttype,
        { list_name: trimmedName },
        { headers }
      );

      const newListType: PropertyList = {
        id: createResponse.data.id,
        list_name: createResponse.data.list_name,
      };

      setLocalSelectedListOverride(newListType.list_name);

      setLocalPropertyLists((prev) => {
        const exists = prev.some((l) => l.id === newListType.id);
        if (exists) return prev;
        return [...prev, newListType];
      });

      setIsCustomList(false);
      setCustomListName("");

      onPropertyListCreated?.(newListType);
      // Pass the freshly created id explicitly — propertyLists in the parent may not
      // have this brand-new entry yet, so a name-only lookup there could miss it.
      updatePropertyList(newListType.list_name, newListType.id);

      toast({
        title: "Custom Tag Created",
        description: `"${newListType.list_name}" has been created and selected`,
      });
    } catch (err: any) {
      console.error("Failed to create custom list:", err);

      const errorMessage =
        err?.response?.data?.list_name?.[0] ||
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        "Failed to create custom tag";

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSavingCustomList(false);
    }
  };

  const cancelCustomList = () => {
    setIsCustomList(false);
    setCustomListName("");
  };

  const handleListSelection = (value: string) => {
    if (readOnly) return;

    if (value === "__custom__") {
      setIsCustomList(true);
      setCustomListName("");
      return;
    }

    setLocalSelectedListOverride(value);
    // Persistence (the PATCH request) happens once, upstream in the parent's
    // onPropertyListChange handler — see updatePropertyList in TemplateBase.
    updatePropertyList(value);
  };

  // ✅ FIXED: Hero Image Upload Handler (same as Template7)
  const handlePrimaryImageChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (readOnly) return;

    const file = e.target.files?.[0];
    if (!file) return;

    // Clear input for re-selection
    e.target.value = "";

    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid File",
        description: "Please upload an image file",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Image must be less than 10 MB",
        variant: "destructive",
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result as string;
      // Validate uploaded image resolution against the container box resolution (if available)
      try {
        const imgEl: HTMLImageElement = await new Promise((resolve, reject) => {
          const tmp = new Image();
          tmp.onload = () => resolve(tmp);
          tmp.onerror = reject;
          tmp.src = dataUrl;
        });

        const w = imgEl.naturalWidth;
        const h = imgEl.naturalHeight;

        if (boxHeroRes) {
          const parts = boxHeroRes.split("×").map((s) => Number(s));
          if (parts.length === 2) {
            const [rw, rh] = parts;
            if (w !== rw || h !== rh) {
              toast({
                title: "Resolution Suggestion",
                description: `Uploaded image is ${w}×${h}. Recommended: ${boxHeroRes}. If possible, upload this resolution for best results.`,
                variant: "default",
              });
            }
          }
        }

        // Show preview immediately after validation
        setFlyerData((prev) => ({ ...prev, heroImage: dataUrl }));
        // This freshly-picked photo becomes the new "original" for this
        // session immediately, so Edit Image always has an untouched source
        // to re-crop from, right from this point on. Mirrors Template1.tsx.
        setOriginalHeroImage(dataUrl);
      } catch (err) {
        toast({
          title: "Image Error",
          description: "Failed to read uploaded image. Please try another file.",
          variant: "destructive",
        });
        return;
      }

      try {
        const token = localStorage.getItem("access_token");
        const formData = new FormData();
        formData.append("image", file);
        // Persist this same freshly-picked photo as the untouched "original"
        // separately from the displayed image, so Edit Image can always
        // re-crop from the full source later instead of re-cropping an
        // already-cropped result. Piggybacks on the PATCH already being sent
        // here (Template3 uploads the hero image immediately, unlike
        // Template1's deferred flow) so there's no extra request.
        formData.append("original_image", file);

        onDirtyChange?.(true);
        onChange?.({ heroImage: dataUrl });

        // Upload to server
        await axiosInstance.patch(
          `${endpoints.properties}${property?.id}/`,
          formData,
          {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              "Content-Type": "multipart/form-data",
            },
          }
        );

        toast({ title: "Success", description: "Main photo updated" });
        onDirtyChange?.(false);
      } catch (err) {
        console.error("Failed to upload hero image:", err);
        toast({
          title: "Error",
          description: "Upload failed",
          variant: "destructive",
          duration: 1000,
        });
      }
    };
    reader.readAsDataURL(file);
  };

  // ─── Edit Image: reposition/zoom the ORIGINAL hero photo (no file picker) ──
  const EDIT_CROP_W = 320;
  const EDIT_CROP_H = 320; // matches the square hero box aspect ratio

  const openEditImage = async () => {
    if (readOnly || !flyerData.heroImage) return;

    let originalSource = originalHeroImage;

    // This component may remount when the hero image changes, resetting local
    // state — so an empty originalHeroImage here doesn't necessarily mean
    // "never captured". Check the server (unaffected by remounts) before
    // assuming there isn't one and falling back to the current display.
    if (!originalSource && property?.id) {
      try {
        const token = localStorage.getItem("access_token");
        const res = await axiosInstance.get(
          `${endpoints.properties}${property.id}/`,
          { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
        );
        originalSource = res?.data?.original_image || "";
      } catch (err) {
        console.error("Failed to check for existing original image:", err);
      }
    }

    if (!originalSource) {
      // Truly never captured yet (new property, or MLS import). Self-heal
      // from the current image so this and every future edit has a stable,
      // full source to re-crop from instead of the current display.
      originalSource = flyerData.heroImage;
      if (property?.id && !flyerData.heroImage.startsWith("data:")) {
        const token = localStorage.getItem("access_token");
        axiosInstance
          .patch(
            `${endpoints.properties}${property.id}/`,
            { original_image: flyerData.heroImage },
            { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
          )
          .catch((err) => {
            console.error("Failed to seed original image:", err);
          });
      }
    }

    setOriginalHeroImage(originalSource);
    setCropModal({ open: true, imageUrl: getEditableImageUrl(originalSource) });
  };

  const handleEditImageConfirm = (croppedDataUrl: string) => {
    setFlyerData((prev) => ({ ...prev, heroImage: croppedDataUrl }));
    onDirtyChange?.(true);
    onChange?.({ heroImage: croppedDataUrl });
    setCropModal({ open: false, imageUrl: "" });
  };

  const handleEditImageCancel = () => {
    setCropModal({ open: false, imageUrl: "" });
  };

  // ─── Edit Image for secondary/gallery photos (same non-destructive pattern
  // as the hero photo above, reusing the shared updatePhotoAtIndex/
  // uploadCroppedPhoto helpers from TemplateBase so original-capture stays
  // centralized) ──
  const SECONDARY_CROP_W = 320;
  const SECONDARY_CROP_H = 320; // matches the square secondary-photo box aspect ratio
  const SECONDARY_INDEX_MAP: Record<"photo2" | "photo3" | "photo4", number> = {
    photo2: 0,
    photo3: 1,
    photo4: 2,
  };

  const [secondaryCropModal, setSecondaryCropModal] = useState<{
    open: boolean;
    imageUrl: string;
    field: "photo2" | "photo3" | "photo4" | null;
  }>({ open: false, imageUrl: "", field: null });

  const openEditSecondaryPhoto = async (
    field: "photo2" | "photo3" | "photo4",
    currentSrc: string
  ) => {
    if (readOnly || !currentSrc) return;

    // Keyed by slot name ("photo2"/"photo3"/"photo4"), not array position —
    // see TemplateBase.tsx's updatePhotoAtIndex for why.
    let originalSource = (flyerData.original_photos && flyerData.original_photos[field]) || "";

    // Check the server too, in case local state doesn't have it yet (e.g. after a remount).
    if (!originalSource && property?.id) {
      try {
        const token = localStorage.getItem("access_token");
        const res = await axiosInstance.get(
          `${endpoints.properties}${property.id}/`,
          { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
        );
        originalSource = res?.data?.original_photos?.[field] || "";
      } catch (err) {
        console.error("Failed to check for existing original photo:", err);
      }
    }

    if (!originalSource) {
      // Truly never captured yet. Self-heal by re-saving the current photo
      // through updatePhotoAtIndex (captureOriginal defaults to true), so
      // this and every future edit has a stable, full source to re-crop
      // from instead of the current display.
      originalSource = currentSrc;
      if (!currentSrc.startsWith("data:")) {
        updatePhotoAtIndex(currentSrc, field).catch((err) => {
          console.error("Failed to seed original photo:", err);
        });
      }
    }

    setSecondaryCropModal({
      open: true,
      imageUrl: getEditableImageUrl(originalSource),
      field,
    });
  };

  const handleSecondaryEditConfirm = async (croppedDataUrl: string) => {
    const field = secondaryCropModal.field;
    if (!field) return;
    try {
      const newUrl = await uploadCroppedPhoto(croppedDataUrl);
      await updatePhotoAtIndex(newUrl, field, { captureOriginal: false });
      toast({ title: "Success", description: "Photo updated" });
    } catch (err) {
      console.error("Failed to save edited photo:", err);
      toast({
        title: "Error",
        description: "Failed to save edited photo",
        variant: "destructive",
      });
    } finally {
      setSecondaryCropModal({ open: false, imageUrl: "", field: null });
    }
  };

  const handleSecondaryEditCancel = () => {
    setSecondaryCropModal({ open: false, imageUrl: "", field: null });
  };

  // ✅ FIXED: Secondary Photo Handler (same as Template7)
  const handleSecondaryFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    field: "photo2" | "photo3" | "photo4"
  ) => {
    if (readOnly) return;

    const file = e.target.files?.[0];
    if (!file) return;

    // Clear input value for re-selection
    e.target.value = "";

    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid File",
        description: "Please upload an image",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Max 10 MB",
        variant: "destructive",
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result as string;
      // Validate uploaded image resolution against the container box resolution (if available)
      try {
        const imgEl: HTMLImageElement = await new Promise((resolve, reject) => {
          const tmp = new Image();
          tmp.onload = () => resolve(tmp);
          tmp.onerror = reject;
          tmp.src = dataUrl;
        });

        const w = imgEl.naturalWidth;
        const h = imgEl.naturalHeight;

        if (boxSecondaryRes) {
          const parts = boxSecondaryRes.split("×").map((s) => Number(s));
          if (parts.length === 2) {
            const [rw, rh] = parts;
            if (w !== rw || h !== rh) {
              toast({
                title: "Resolution Suggestion",
                description: `Uploaded image is ${w}×${h}. Recommended: ${boxSecondaryRes}. If possible, upload this resolution for best results.`,
                variant: "default",
              });
            }
          }
        }

        // Show preview immediately after validation
        setFlyerData((prev) => ({ ...prev, [field]: dataUrl }));
      } catch (err) {
        toast({
          title: "Image Error",
          description: "Failed to read uploaded image. Please try another file.",
          variant: "destructive",
        });
        return;
      }

      try {
        const token = localStorage.getItem("access_token");
        const formData = new FormData();
        formData.append("image", file);

        // Upload to server
        const res = await axiosInstance.post(endpoints.mls_uploads, formData, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            "Content-Type": "multipart/form-data",
          },
        });

        if (res?.data?.url) {
          // Update with server URL using updatePhotoAtIndex
          updatePhotoAtIndex(res.data.url, field);
          toast({ title: "Success", description: "Photo uploaded", duration: 1000 });
        }
      } catch (err) {
        console.error("Failed to upload photo:", err);
        toast({
          title: "Error",
          description: "Upload failed",
          variant: "destructive",
          duration: 1000,
        });
      }
    };
    reader.readAsDataURL(file);
  };

  // ✅ ADD: Remove Hero Photo Handler
  const handleRemoveHeroPhoto = () => {
    if (readOnly) return;
    setFlyerData((prev) => ({ ...prev, heroImage: "" }));
    onDirtyChange?.(true);
    onChange?.({ heroImage: "" });
    toast({ title: "Removed", description: "Main photo removed" });
  };

  // ✅ ADD: Remove Secondary Photo Handler (same as Template7)
  const handleRemoveSecondaryPhoto = (
    field: "photo2" | "photo3" | "photo4"
  ) => {
    if (readOnly) return;
    removePhotoAtIndex(field);
    toast({ title: "Removed", description: "Photo removed" });
  };

  // ✅ ADD: Helper function to get photo from multiple sources (same as Template7)
  const getPhotoSrc = (
    field: "photo2" | "photo3" | "photo4",
    index: number
  ): string => {
    // 1. Check flyerData field directly (e.g., flyerData.photo2)
    const fieldValue = flyerData[field];
    if (
      fieldValue &&
      typeof fieldValue === "string" &&
      fieldValue.trim() !== ""
    ) {
      return fieldValue;
    }

    // 2. Check flyerData.photos array
    if (Array.isArray(flyerData.photos) && flyerData.photos[index]) {
      const photoFromArray = flyerData.photos[index];
      if (
        photoFromArray &&
        typeof photoFromArray === "string" &&
        photoFromArray.trim() !== ""
      ) {
        return photoFromArray;
      }
    }

    // 3. Check property.photos array as fallback
    if (property && Array.isArray(property.photos) && property.photos[index]) {
      const propertyPhoto = property.photos[index];
      if (
        propertyPhoto &&
        typeof propertyPhoto === "string" &&
        propertyPhoto.trim() !== ""
      ) {
        return propertyPhoto;
      }
    }

    return "";
  };

  // ✅ For Template3, also handle secondaryImage which maps to photo2 (index 0)
  const getSecondaryImageSrc = (): string => {
    // Check secondaryImage first
    if (
      flyerData.secondaryImage &&
      typeof flyerData.secondaryImage === "string" &&
      flyerData.secondaryImage.trim() !== ""
    ) {
      return flyerData.secondaryImage;
    }

    // Fall back to photo2/photos[0]
    return getPhotoSrc("photo2", 0);
  };

  return (
    <div
      className="overflow-x-auto w-full"
      style={{
        WebkitOverflowScrolling: "touch",
        transform: "rotateX(180deg)",
      }}
    >
      <div className="bg-gradient-to-b from-[#e8f4fc] via-[#f0f9ff] to-white shadow-none rounded-3xl overflow-hidden font-arial relative border mx-auto inline-block align-top"
        style={{
          minWidth: "840px",
          transform: "rotateX(180deg)",
        }}>
        {/* Custom Fields Editor Modal */}
        {editingCustom3 && !readOnly && (
          <div className="absolute right-6 top-20 z-50 bg-white p-4 rounded-lg shadow-xl w-80 border border-sky-200">
            <div className="flex items-center justify-between mb-3">
              <strong className="text-sky-900">Custom Fields</strong>
              <div className="flex gap-2">
                <button
                  onClick={addCustomField3}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Add
                </button>
                <button
                  onClick={() => setEditingCustom3(false)}
                  className="text-sm text-gray-600 hover:text-gray-800"
                >
                  Close
                </button>
              </div>
            </div>
            <div className="max-h-48 overflow-auto">
              {customFields3.map((f, idx) => (
                <div key={idx} className="flex gap-2 items-center mb-2">
                  <input
                    value={f.key}
                    onChange={(e) =>
                      updateCustomField3(idx, "key", e.target.value)
                    }
                    placeholder="Key"
                    className="border px-2 py-1 flex-1 text-sm rounded"
                  />
                  <input
                    value={f.value}
                    onChange={(e) =>
                      updateCustomField3(idx, "value", e.target.value)
                    }
                    placeholder="Value"
                    className="border px-2 py-1 flex-1 text-sm rounded"
                  />
                  <button
                    onClick={() => removeCustomField3(idx)}
                    className="text-red-600 hover:text-red-800"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-3 flex justify-end">
              <button
                onClick={saveCustomForTemplate3}
                className="bg-green-600 text-white px-4 py-1.5 rounded text-sm hover:bg-green-700"
              >
                Save
              </button>
            </div>
          </div>
        )}

        {/* Header with Tag Selector */}
        <div className="relative bg-gradient-to-r from-[#0ea5e9] to-[#0284c7] px-6 md:px-10 py-6">
          <div className="flex flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <h2 className="text-xl md:text-2xl font-bold text-white truncate">
                {flyerData.companyName || agentData?.company || ""}
              </h2>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <Tag className="h-6 w-6 md:h-7 md:w-7 text-white flex-shrink-0" />
              <div className="flex items-center bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 border border-white/30">
                {/* ✅ ADD READONLY CHECK - same as template1/template2 */}
                {readOnly ? (
                  <span className="text-white font-semibold text-sm uppercase tracking-wide">
                    {listSelectValue || "Just Listed"}
                  </span>
                ) : isCustomList ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={customListName}
                      onChange={(e) => setCustomListName(e.target.value.slice(0, 20))}
                      placeholder="Custom tag (max 20 chars)"
                      maxLength={20}
                      className="bg-white/20 text-white placeholder-white/60 px-2 py-1 rounded text-sm w-28 md:w-32 outline-none border border-white/30 focus:border-white/60"
                      autoFocus
                      disabled={isSavingCustomList}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !isSavingCustomList) {
                          e.preventDefault();
                          saveCustomList();
                        }
                        if (e.key === "Escape") {
                          cancelCustomList();
                        }
                      }}
                    />
                    <button
                      onClick={saveCustomList}
                      disabled={!customListName.trim() || isSavingCustomList}
                      className="p-1.5 bg-white/20 rounded-full hover:bg-white/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="Save custom tag"
                    >
                      {isSavingCustomList ? (
                        <Loader2 className="h-4 w-4 text-white animate-spin" />
                      ) : (
                        <Check className="h-4 w-4 text-white" />
                      )}
                    </button>
                    <button
                      onClick={cancelCustomList}
                      disabled={isSavingCustomList}
                      className="p-1.5 bg-white/20 rounded-full hover:bg-white/30 disabled:opacity-50 transition-colors"
                      title="Cancel"
                    >
                      <X className="h-4 w-4 text-white" />
                    </button>
                  </div>
                ) : (
                  <select
                    value={listSelectValue}
                    onChange={(e) => handleListSelection(e.target.value)}
                    className="bg-transparent text-white px-2 py-1 rounded font-semibold text-sm border-0 outline-none cursor-pointer"
                  >
                    {allLists
                      .filter((l) => l.list_name !== "Open House")
                      .map((l) => (
                        <option
                          key={l.id}
                          value={l.list_name}
                          className="text-gray-900 bg-white"
                        >
                          {l.list_name}
                        </option>
                      ))}

                    <option
                      value="__custom__"
                      className="text-gray-900 bg-white font-medium"
                    >
                      + Add Custom Tag
                    </option>
                  </select>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ✅ FIXED: TWO PHOTOS SECTION with proper photo sync */}
        <div className="p-8">
          {/* <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-4"> */}
          <div className="grid grid-cols-2 gap-4 justify-items-center">
            {/* Main Photo (Hero Image) */}
            {/* <div className={`relative w-full rounded-2xl overflow-hidden bg-gradient-to-br from-sky-50 to-sky-100 group h-[260px] md:h-[280px] ring-2 ring-sky-200 shadow-md ${!readOnly ? 'border border-sky-200' : 'border border-sky-100'}`}> */}
            <div
              ref={heroContainerRef}
              className={`relative rounded-2xl overflow-hidden bg-gradient-to-br from-sky-50 to-sky-100 group ring-2 ring-sky-200 shadow-md ${!readOnly ? "border border-sky-200" : "border border-sky-100"
                }`}
              style={{
                width: "380px",
                height: "380px",
                minWidth: "380px",
                maxWidth: "380px",
                minHeight: "380px",
                maxHeight: "380px",
              }}
            >
              {/* Resolution Hint Overlay */}
              {!readOnly && (
                <div className="absolute bottom-3 left-3 z-40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                  <div className="bg-white/90 text-sky-700 text-xs rounded-xl px-3 py-2 border border-sky-100 max-w-[320px]">
                    {boxHeroRes ? (
                      <div className="leading-snug">
                        <div className="font-semibold">Required image resolution: {boxHeroRes}</div>
                        <div className="mt-1 text-[11px]">Please upload exactly this resolution.</div>
                      </div>
                    ) : (
                      <div className="leading-snug">{RESOLUTION_HINT}</div>
                    )}
                  </div>
                </div>
              )}

              {isOpenHouseConfirmed && (
                <div className="absolute top-1 left-2 z-20">
                  <div className="relative w-32">
                    <img
                      src={openHouseBadge}
                      alt="Open House"
                      className="w-full drop-shadow-xl"
                    />
                    <span
                      className="absolute inset-0 flex items-center justify-center
                   text-white font-bold text-sm
                   tracking-wide text-center pointer-events-none"
                    >
                      OPEN
                      <br />
                      HOUSE
                    </span>
                  </div>
                </div>
              )}

              {flyerData.heroImage ? (
                <>
                  <img
                    src={flyerData.heroImage}
                    alt="Main property"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      console.error(
                        "Failed to load hero image:",
                        flyerData.heroImage
                      );
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                  {/* ✅ Only show hover overlay when NOT readOnly */}
                  {!readOnly && (
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center gap-3">
                      <Button
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        className="bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Change
                      </Button>
                      <Button
                        size="sm"
                        onClick={openEditImage}
                        className="bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30"
                      >
                        <Crop className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                    </div>
                  )}
                </>
              ) : !readOnly ? (
                <label
                  htmlFor="hero-upload-t3"
                  className="flex flex-col items-center justify-center h-full text-sky-600 hover:text-sky-800 cursor-pointer transition-all duration-300"
                >
                  <ImageIcon className="h-20 w-20 mb-4" />
                  <p className="text-2xl font-bold">
                    Upload Main Photo
                  </p>
                  <p className="text-sm mt-2 text-sky-500">Click or drag image</p>
                </label>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-sky-600">
                  <ImageIcon className="h-20 w-20 mb-4" />
                  <p className="text-2xl font-bold">No Image</p>
                </div>
              )}
            </div>

            {/* ✅ FIXED: Secondary Photo with proper photo sync */}
            {/* <div className={`relative w-full rounded-2xl overflow-hidden bg-gradient-to-br from-sky-50 to-sky-100 group h-[260px] md:h-[280px] ring-2 ring-sky-200 shadow-md ${!readOnly ? 'border border-sky-200' : 'border border-sky-100'}`}> */}
            <div
              ref={secondaryContainerRef}
              className={`relative rounded-2xl overflow-hidden bg-gradient-to-br from-sky-50 to-sky-100 group ring-2 ring-sky-200 shadow-md ${!readOnly ? "border border-sky-200" : "border border-sky-100"
                }`}
              style={{
                width: "380px",
                height: "380px",
                minWidth: "380px",
                maxWidth: "380px",
                minHeight: "380px",
                maxHeight: "380px",
              }}
            >
              {/* Resolution Hint Overlay */}
              {!readOnly && (
                <div className="absolute bottom-3 left-3 z-40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                  <div className="bg-white/90 text-sky-700 text-xs rounded-xl px-3 py-2 border border-sky-100 max-w-[320px]">
                    {boxSecondaryRes ? (
                      <div className="leading-snug">
                        <div className="font-semibold">Required image resolution: {boxSecondaryRes}</div>
                        <div className="mt-1 text-[11px]">Please upload exactly this resolution.</div>
                      </div>
                    ) : (
                      <div className="leading-snug">{RESOLUTION_HINT}</div>
                    )}
                  </div>
                </div>
              )}
              {(() => {
                const secondarySrc = getSecondaryImageSrc();
                return secondarySrc ? (
                  <>
                    <img
                      src={secondarySrc}
                      alt="Secondary view"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        console.error(
                          "Failed to load secondary image:",
                          secondarySrc
                        );
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                    {/* ✅ Only show hover overlay when NOT readOnly */}
                    {!readOnly && (
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center gap-3">
                        <Button
                          size="sm"
                          onClick={() => photo2Ref.current?.click()}
                          className="bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Change
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => openEditSecondaryPhoto("photo2", secondarySrc)}
                          className="bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30"
                        >
                          <Crop className="h-4 w-4 mr-2" />
                          Edit
                        </Button>
                      </div>
                    )}
                  </>
                ) : !readOnly ? (
                  <label
                    htmlFor="secondary-upload-t3"
                    className="flex flex-col items-center justify-center h-full text-sky-600 hover:text-sky-800 cursor-pointer transition-all duration-300"
                  >
                    <ImageIcon className="h-20 w-20 mb-4" />
                    <p className="text-2xl font-bold">
                      Upload Second Photo
                    </p>
                    {/* <p className="text-sm mt-2 text-sky-500">(Optional)</p> */}
                  </label>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-sky-600">
                    <ImageIcon className="h-20 w-20 mb-4" />
                    <p className="text-2xl font-bold">No Image</p>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Hidden File Inputs - Only render when NOT readOnly */}
          {!readOnly && (
            <>
              <input
                id="hero-upload-t3"
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePrimaryImageChange}
              />
              <input
                id="secondary-upload-t3"
                ref={photo2Ref}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  // Handle secondary image - also update secondaryImage field
                  const file = e.target.files?.[0];
                  if (!file) return;

                  e.target.value = "";

                  if (!file.type.startsWith("image/")) {
                    toast({
                      title: "Invalid File",
                      description: "Please upload an image file",
                      variant: "destructive",
                    });
                    return;
                  }

                  if (file.size > 10 * 1024 * 1024) {
                    toast({
                      title: "File Too Large",
                      description: "Image must be less than 10 MB",
                      variant: "destructive",
                    });
                    return;
                  }

                  const reader = new FileReader();
                  reader.onload = async () => {
                    const dataUrl = reader.result as string;
                    // Update both secondaryImage and photo2 for immediate preview
                    setFlyerData((prev) => ({
                      ...prev,
                      secondaryImage: dataUrl,
                      photo2: dataUrl,
                    }));

                    try {
                      const token = localStorage.getItem("access_token");
                      const formData = new FormData();
                      formData.append("image", file);

                      const uploadRes = await axiosInstance.post(
                        endpoints.mls_uploads,
                        formData,
                        {
                          headers: {
                            ...(token ? { Authorization: `Bearer ${token}` } : {}),
                            "Content-Type": "multipart/form-data",
                          },
                        }
                      );

                      if (uploadRes?.data?.url) {
                        const imageUrl = uploadRes.data.url;
                        // Update with server URL
                        setFlyerData((prev) => ({
                          ...prev,
                          secondaryImage: imageUrl,
                        }));
                        updatePhotoAtIndex(imageUrl, "photo2");
                        toast({
                          title: "Success",
                          description: "Secondary photo uploaded",
                          duration: 1000,
                        });
                      }
                    } catch (err) {
                      console.error("Failed to upload secondary image:", err);
                      toast({
                        title: "Upload Error",
                        description: "Failed to upload photo",
                        variant: "destructive",
                      });
                    }
                  };
                  reader.readAsDataURL(file);
                }}
              />
              <ImageCropModal
                open={cropModal.open}
                imageUrl={cropModal.imageUrl}
                title="Adjust Property Photo"
                outputSize={700}
                cropW={EDIT_CROP_W}
                cropH={EDIT_CROP_H}
                onConfirm={handleEditImageConfirm}
                onCancel={handleEditImageCancel}
              />
              <ImageCropModal
                open={secondaryCropModal.open}
                imageUrl={secondaryCropModal.imageUrl}
                title="Adjust Property Photo"
                outputSize={700}
                cropW={SECONDARY_CROP_W}
                cropH={SECONDARY_CROP_H}
                onConfirm={handleSecondaryEditConfirm}
                onCancel={handleSecondaryEditCancel}
              />
            </>
          )}

          {/* Custom fields (read-only display) from PropertyListType */}
          {property?.propertyListType?.custom_template_data?.template3 &&
            Array.isArray(
              property.propertyListType.custom_template_data.template3
            ) &&
            property.propertyListType.custom_template_data.template3.length >
            0 && (
              <div className="mt-6">
                <div className="bg-white rounded-lg p-4 border border-sky-100">
                  <h4 className="font-semibold text-sm text-sky-700 mb-2">
                    Custom Fields
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700">
                    {property.propertyListType.custom_template_data.template3.map(
                      (f: any, i: number) => (
                        <div key={i} className="flex justify-between">
                          <span className="font-medium">{f.key}</span>
                          <span>{f.value}</span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            )}
        </div>

        {/* Price & Stats Card */}
        <div className="px-4 sm:px-6 md:px-10 pb-6">
          <div className="bg-white rounded-3xl shadow-2xl p-4 sm:p-6 md:p-8 border border-sky-100 overflow-hidden">

            {/* Main Layout */}
            <div className="flex flex-row items-start justify-between gap-6">

              {/* Price + Address */}
              <div className="flex-1 min-w-0">
                {readOnly ? (
                  <p
                    className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-[#0c4a6e] break-words leading-tight"
                  >
                    {flyerData.price
                      ? "$" + Number(flyerData.price).toLocaleString()
                      : "$0"}
                  </p>
                ) : (
                  <input
                    type="text"
                    value={
                      flyerData.price
                        ? "$" + Number(flyerData.price).toLocaleString()
                        : ""
                    }
                    onChange={(e) => {
                      const num = e.target.value.replace(/[^0-9]/g, "");
                      handleInputChange("price", num ? Number(num) : null);
                    }}
                    placeholder="$1,295,000"
                    className="w-full bg-transparent border-none outline-none text-[#0c4a6e] placeholder-[#0c4a6e]/40 font-black leading-tight break-words text-3xl sm:text-4xl md:text-5xl lg:text-6xl"
                  />
                )}

                {/* Address */}
                <div className="flex items-start gap-2 text-sky-700 mt-4 min-w-0">
                  <MapPin className="h-5 w-5 md:h-6 md:w-6 flex-shrink-0 mt-1" />

                  {readOnly ? (
                    <p
                      className="
                text-sm
                sm:text-base
                md:text-lg
                font-medium
                break-words
                leading-relaxed
              "
                    >
                      {flyerData.address || "No address provided"}
                    </p>
                  ) : (
                    <textarea
                      value={flyerData.address || ""}
                      onChange={(e) =>
                        handleInputChange("address", e.target.value)
                      }
                      placeholder="123 Ocean Drive, Miami Beach, FL"
                      rows={3}
                      className="
                flex-1
                resize-none
                bg-transparent
                border-none
                outline-none
                text-sm
                sm:text-base
                md:text-lg
                font-medium
                placeholder-sky-600/50
                leading-relaxed
                break-words
              "
                    />
                  )}
                </div>
              </div>

              {/* Stats Section */}
              <div
                className="
          grid
          grid-cols-3
          gap-4
          sm:gap-6
          md:gap-8
          shrink-0
        "
              >
                {[
                  {
                    icon: Bed,
                    value: flyerData.bedrooms,
                    label: "Beds",
                    field: "bedrooms" as const,
                  },
                  {
                    icon: Bath,
                    value: flyerData.bathrooms,
                    label: "Baths",
                    field: "bathrooms" as const,
                  },
                  {
                    icon: Square,
                    value: flyerData.sqft,
                    label: "Sq Ft",
                    field: "sqft" as const,
                  },
                ].map(({ icon: Icon, value, label, field }) => (
                  <div
                    key={field}
                    className="
              flex
              flex-col
              items-center
              justify-center
              text-center
              min-w-0
            "
                  >
                    <Icon className="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7 text-[#0ea5e9] mb-2" />

                    {readOnly ? (
                      <p
                        className="
                  text-xl
                  sm:text-2xl
                  md:text-3xl
                  font-black
                  text-[#0c4a6e]
                  leading-none
                  break-words
                "
                      >
                        {value ?? 0}
                      </p>
                    ) : (
                      <input
                        type="number"
                        min="0"
                        value={value ?? ""}
                        onChange={(e) =>
                          handleInputChange(
                            field,
                            e.target.value ? Number(e.target.value) : null
                          )
                        }
                        className="
                  w-full
                  max-w-[90px]
                  text-center
                  bg-transparent
                  border-none
                  outline-none
                  font-black
                  text-[#0c4a6e]
                  leading-none
                  text-xl
                  sm:text-2xl
                  md:text-3xl
                  [-moz-appearance:textfield]
                  [&::-webkit-outer-spin-button]:appearance-none
                  [&::-webkit-inner-spin-button]:appearance-none
                "
                        placeholder="0"
                      />
                    )}

                    <p className="text-xs sm:text-sm text-gray-600 font-medium mt-2">
                      {label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="px-6 md:px-10 pb-10 space-y-8 md:space-y-10">
          {/* Headline */}
          <div className="text-center">
            {readOnly ? (
              <p className="text-3xl md:text-4xl lg:text-5xl font-black text-[#0c4a6e] text-center">
                {flyerData.headline || "Stunning Oceanfront Residence"}
              </p>
            ) : (
              <textarea
                value={flyerData.headline || ""}
                onChange={(e) => handleInputChange("headline", e.target.value)}
                placeholder="Stunning Oceanfront Residence with Panoramic Views"
                className="text-2xl md:text-4xl lg:text-5xl font-black text-[#0c4a6e] bg-transparent border-none outline-none w-full text-center placeholder-[#0c4a6e]/40"
                style={{ textWrap: "wrap" } as React.CSSProperties}
              />
            )}
          </div>

          {/* Description */}
          <div className="text-center max-w-3xl mx-auto">
            {readOnly ? (
              <p className="text-base md:text-lg text-gray-700 leading-relaxed text-center whitespace-pre-wrap">
                {flyerData.description || "No description provided."}
              </p>
            ) : (
              <textarea
                value={flyerData.description || ""}
                onChange={(e) => handleInputChange("description", e.target.value)}
                placeholder="Experience coastal luxury at its finest in this architectural masterpiece. This stunning residence offers breathtaking ocean views, world-class amenities, and sophisticated design throughout."
                rows={4}
                className="w-full text-base md:text-lg text-gray-700 leading-relaxed bg-transparent border-none outline-none resize-none text-center placeholder-gray-400"
              />
            )}
          </div>

          {/* Key Features */}
          <div className="max-w-4xl mx-auto">
            <h3 className="text-xl md:text-2xl font-bold text-[#0c4a6e] text-center mb-6 flex items-center justify-center gap-3">
              <span>Key Features & Amenities</span>
              {!readOnly && (
                <span className="text-sm font-normal text-gray-500">
                  ({flyerData.features.length}/10)
                </span>
              )}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {flyerData.features.map((f, i) => (
                <div
                  key={f.id ?? i}
                  className="bg-gradient-to-br from-sky-50 to-blue-50 border border-sky-200 rounded-xl px-4 md:px-5 py-3 md:py-4 flex items-center gap-3 hover:shadow-md transition-all duration-300 group min-w-0"
                >
                  <div className="w-2.5 h-2.5 md:w-3 md:h-3 bg-[#0ea5e9] rounded-full flex-shrink-0" />
                  {readOnly ? (
                    <span className="text-sm md:text-base text-gray-800 min-w-0 break-words">
                      {f.feature_name || "Feature"}
                    </span>
                  ) : (
                    <>
                      <input
                        type="text"
                        value={f.feature_name}
                        onChange={(e) => handleFeatureChange(i, e.target.value)}
                        onBlur={() => handleFeatureBlur(i)}
                        maxLength={30}
                        className="flex-1 min-w-0 bg-transparent border-none outline-none text-sm md:text-base text-gray-800 placeholder-gray-400"
                        placeholder="Hardwood Floors (max 30 chars)..."
                      />
                      <button
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleFeatureDelete(i);
                        }}
                        onTouchStart={(e) => {
                          e.preventDefault();
                          handleFeatureDelete(i);
                        }}
                        className="text-red-500 hover:text-red-700 text-xl font-bold opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                        aria-label="Delete feature"
                      >
                        ×
                      </button>
                    </>
                  )}
                </div>
              ))}

              {!readOnly && flyerData.features.length < 10 && (
                <button
                  onClick={addFeature}
                  className="bg-sky-50 border-2 border-dashed border-sky-300 rounded-xl px-4 md:px-5 py-3 md:py-4 text-sky-600 font-medium hover:bg-sky-100 hover:border-sky-400 transition-all duration-300 text-sm md:text-base"
                >
                  + Add Feature
                </button>
              )}

              {!readOnly && flyerData.features.length >= 10 && (
                <p className="col-span-full text-center text-sm text-gray-500 italic">
                  Maximum of 10 features reached
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-row items-center gap-5 md:gap-6 bg-gradient-to-br from-sky-50 to-blue-50 border border-sky-200 rounded-xl px-3 py-3 md:px-3 md:py-3">

            {/* Agent Avatar - LEFT */}
            <div className="w-[150px] h-[180px] rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0">
              {agentData?.avatar ? (
                <img src={agentData.avatar} alt="Agent" className="w-full h-full object-cover rounded-xl" />
              ) : (
                <span className="text-xs text-gray-400 text-center px-1">Agent Photo</span>
              )}
            </div>

            {/* Agent Info - CENTER */}
            <div className="flex-1 text-center min-w-0">
              <p className="text-xl md:text-2xl font-extrabold text-[#0c4a6e] mb-1">
                {flyerData.agentName || "Agent Name"}
              </p>

              <div className="flex flex-col gap-2 text-sm md:text-base text-gray-700 min-w-0 items-center">

                {/* Phone */}
                <div className="inline-flex items-center gap-1.5 mx-auto">
                  <Phone className="h-4 w-4 md:h-5 md:w-5 text-[#0ea5e9] flex-shrink-0" />
                  <span className="font-medium">{flyerData.agentPhone || "N/A"}</span>
                </div>

                {/* Email */}
                <div className="inline-flex items-center gap-1.5 mx-auto pl-5">
                  <Mail className="h-4 w-4 md:h-5 md:w-5 text-[#0ea5e9] underline  flex-shrink-0" />
                  <span className="font-medium underline">
                    {/* {flyerData.agentEmail || "N/A"} */}
                     Click here to email Me
                    </span>
                   
                </div>

                {/* Company Name */}
                <div className="inline-flex items-center gap-1.5 mx-auto">
                  <Building2 className="h-4 w-4 md:h-5 md:w-5 text-[#0ea5e9] flex-shrink-0" />
                  <span className="font-medium">{flyerData.companyName || agentData?.company || "N/A"}</span>
                </div>

                {/* License Number */}
                {agentData?.license_number && (
                  <div className="inline-flex items-center gap-1.5 mx-auto pl-5">
                    <IdCard className="h-4 w-4 md:h-5 md:w-5 text-[#0ea5e9] flex-shrink-0" />
                    <span className="font-medium">
                      License #: {agentData.license_number}
                    </span>
                  </div>
                )}

              </div>
            </div>

            {/* Company Logo - RIGHT */}
            <div className="w-[180px] h-[180px] rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0">
              {agentData?.company_logo ? (
                <img src={agentData.company_logo} alt="Company Logo" className="w-full h-full object-cover rounded-xl" />
              ) : (
                <span className="text-xs text-gray-400 text-center px-1">Company Logo</span>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Template3;
